import { useState } from 'react';
import { useRouter } from 'next/router';
import { useTranslation } from '../../../lib/i18n';
import { contactThreadPath, useContactBook } from '../../../lib/contact-book';
import { outreachCard, outreachContext, type OutreachContextEntry } from '../../../lib/island-quickview';
import { askKind, type WebOpenAsk } from '../../../lib/open-asks';
import { useAgentName } from '../../../lib/use-agent-name';
import { useGenericBi } from '../../../lib/use-generic-bi';
import { TASKS_PAGE } from '../../../lib/tasks-bi';
import { messageDir } from '../../../lib/message-direction';
import { QuickViewRowButton } from '../AgentsIslandQuickViewRow';
import { Card, TextButton } from '../ui';
import { useCategoryRowClass } from '../category-view/CategoryView';
import { TaskDecisionPrompts } from './TaskDecisionPrompts';
import type { ActionNeededCard as Item } from './tasks-list-logic';
import styles from './tasks.module.css';

/**
 * ONE card of the Action needed tab that is not a task row (`actionNeededCards`), slotted among the rows by
 * recency:
 *  - an OUTREACH-ONLY question — the island's own outreach card (person and channel, the thread, the question with
 *    its chips, "Open conversation" to the thread);
 *  - an OPEN ASK (`lib/open-asks.ts`) — a Decision answers in place through the SAME `TaskDecisionPrompts` block
 *    every question card wears; a ledger ask shows what the push said. Both end in "Open conversation", to the
 *    chat the ask was put in.
 * Answering re-reads `/api/tasks` (the block's `['tasks']` invalidation), which drops the card on every screen.
 */
export function ActionNeededCard({ item }: { item: Item }) {
  const rowSurface = useCategoryRowClass();
  const key = item.kind === 'outreach' ? item.question.decisionId : item.ask.id;
  return (
    <div className={`${styles.cardRow}${rowSurface ? ` ${styles.cardRowInList}` : ''}`} data-tasks-action-needed={key}>
      <Card className={`${styles.card}${rowSurface ? ` ${rowSurface}` : ''}`}>
        {item.kind === 'outreach' ? <OutreachQuestion item={item} /> : <OpenAsk ask={item.ask} />}
      </Card>
    </div>
  );
}

function OutreachQuestion({ item }: { item: Extract<Item, { kind: 'outreach' }> }) {
  const { t, i18n } = useTranslation();
  const router = useRouter();
  const { logPageClickBi } = useGenericBi();
  const contactBook = useContactBook('');
  const contacts = (contactBook.kind === 'ready' ? contactBook.contacts : []) as OutreachContextEntry[];
  const outreach = outreachContext(item.question, contacts, t('contacts.row.unnamed'), i18n.language);
  return (
    // An outreach-only card has no row button, so `onOpen` is never reached; "Open conversation" is the way in.
    <QuickViewRowButton
      task={outreachCard(item.question)}
      onOpen={() => undefined}
      outreach={outreach}
      page={TASKS_PAGE}
      onOpenConversation={(ctx) => {
        logPageClickBi({ ...TASKS_PAGE, button_name: 'open_conversation_from_question' }, { additionalFields: { outreach_id: ctx.outreachId } });
        void router.push(contactThreadPath(ctx.identity, ctx.outreachId));
      }}
    />
  );
}

function OpenAsk({ ask }: { ask: WebOpenAsk }) {
  const { t } = useTranslation();
  const router = useRouter();
  const maestro = useAgentName();
  const { logPageClickBi } = useGenericBi();
  // Only a signal to re-render after an answer lands; the row re-reads from the cache.
  const [, setAnsweredHere] = useState(false);
  const fallback = t('outreach.line.needsYouFallback', { maestroAgentName: maestro.name });
  const open = () => {
    logPageClickBi({ ...TASKS_PAGE, button_name: 'open_conversation_from_ask' }, { additionalFields: { ask_kind: askKind(ask) } });
    void router.push(`/?conversation=${encodeURIComponent(ask.conversationId)}`);
  };
  const link = (
    <span style={{ display: 'flex' }}>
      <TextButton size="small" underline onClick={open}>
        {t('outreach.island.openConversation')}
      </TextButton>
    </span>
  );
  if (ask.prompt) {
    const prompt = ask.prompt.question?.trim() ? ask.prompt : { ...ask.prompt, question: fallback };
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {/* No task behind it (`id: ''`): the verdict carries none and the BI no `task_id`. A goal question's
            `goalId` is what the answer flow writes the verdict against. */}
        <TaskDecisionPrompts
          task={{ id: '', goalId: ask.goalId }}
          prompts={[prompt]}
          origin={null}
          onAnsweredChange={setAnsweredHere}
          page={TASKS_PAGE}
          pillChips
          rowKey={ask.id}
        />
        {link}
      </div>
    );
  }
  const title = ask.title || (ask.body ? undefined : fallback);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <button type="button" onClick={open} className={styles.askNotice}>
        {title ? (
          <span className={styles.askTitle} dir={messageDir(title)}>
            {title}
          </span>
        ) : null}
        {ask.body ? (
          <span className={styles.askBody} dir={messageDir(ask.body)}>
            {ask.body}
          </span>
        ) : null}
      </button>
      {link}
    </div>
  );
}
